import { apiUrl } from "../utils/api";
import type { OfficeEditorConfig } from "./officeEditingService";
import type { ParsedMailFile } from "./mailFileService";

/**
 * Die Ablage eines Kundenakten-Ordners: Dateien des alten Shares, wie sie im
 * Objektspeicher liegen (Plan 01.10.2026, Teil A3/B2). Explorer-Ebenen, der
 * Ordnerbaum und der Inhalt einzelner Dateien — ohne Zuweisung, ohne
 * `Document`-Zeile. Spiegel der Endpunkte in
 * `apps/api/src/kundenakten-ablage.controller.ts`.
 */
export type AblageOrdner = {
  name: string;
  /** Pfad ohne abschließenden `/`. */
  pfad: string;
  /** Dateien darunter, rekursiv. */
  dateien: number;
  bytes: number;
  geaendertAt: string | null;
};

export type AblageDatei = {
  id: number;
  fileName: string;
  relPath: string;
  sizeBytes: number;
  modifiedAt: string | null;
  mimeType: string | null;
  isSchaden: boolean;
};

export type AblageEbene = { pfad: string; ordner: AblageOrdner[]; dateien: AblageDatei[] };

const gepruft = async (res: Response, was: string): Promise<Response> => {
  if (!res.ok) throw new Error(`${was} (HTTP ${res.status})`);
  return res;
};

export const kundenaktenAblageService = {
  /** Eine Explorer-Ebene; `pfad` ohne abschließenden `/`, leer = Wurzel. */
  async ebene(folderId: number, pfad = ""): Promise<AblageEbene> {
    const res = await gepruft(
      await fetch(apiUrl(`kundenakten-folders/${folderId}/ablage`, pfad ? { pfad } : undefined)),
      "Ordner konnte nicht geladen werden",
    );
    return res.json();
  },

  /** Alle Ordnerpfade (für den Baum) und die Gesamtzahl der Dateien. */
  async baum(folderId: number): Promise<{ ordner: string[]; dateien: number }> {
    const res = await gepruft(await fetch(apiUrl(`kundenakten-folders/${folderId}/ablage/baum`)), "Ordnerbaum konnte nicht geladen werden");
    return res.json();
  },

  /** Inhalt als Blob (für die Vorschau im Fenster). */
  async blob(dateiId: number): Promise<Blob> {
    const res = await gepruft(await fetch(apiUrl(`kundenakten-ablage/${dateiId}/inhalt`)), "Datei konnte nicht geladen werden");
    return res.blob();
  },

  /** Signierter Link; mit `inline` zeigt der Browser PDF und Bilder selbst. */
  async signierterLink(dateiId: number, inline = false): Promise<string> {
    const res = await gepruft(
      await fetch(apiUrl(`kundenakten-ablage/${dateiId}/download-link`), { method: "POST" }),
      "Link konnte nicht erzeugt werden",
    );
    const { url } = (await res.json()) as { url: string };
    return inline ? `${url}&inline=1` : url;
  },

  /**
   * Herunterladen über den signierten Link. Der Klick auf den unsichtbaren
   * Anker ist same-origin und braucht kein Popup — ein `window.open` nach dem
   * `await` würde der Blocker verschlucken.
   */
  async herunterladen(dateiId: number, fileName: string): Promise<void> {
    const url = await kundenaktenAblageService.signierterLink(dateiId);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  },

  /** Im neuen Tab öffnen (PDF/Bild inline): das Fenster VOR dem `await` aufmachen. */
  async imTabOeffnen(dateiId: number): Promise<void> {
    const fenster = window.open("", "_blank", "noopener");
    try {
      const url = await kundenaktenAblageService.signierterLink(dateiId, true);
      if (fenster) fenster.location.href = url;
      else window.location.href = url;
    } catch (e) {
      fenster?.close();
      throw e;
    }
  },

  /** ONLYOFFICE-Konfiguration (nur lesen). */
  async officeConfig(dateiId: number): Promise<OfficeEditorConfig> {
    const res = await gepruft(await fetch(apiUrl(`kundenakten-ablage/${dateiId}/office/config`)), "Editor konnte nicht geöffnet werden");
    return res.json();
  },

  async mailParsed(dateiId: number): Promise<ParsedMailFile> {
    const res = await gepruft(await fetch(apiUrl(`kundenakten-ablage/${dateiId}/mail`)), "Mail konnte nicht gelesen werden");
    return res.json();
  },

  async mailAttachmentBlob(dateiId: number, index: number): Promise<Blob> {
    const res = await gepruft(
      await fetch(apiUrl(`kundenakten-ablage/${dateiId}/mail/anhang/${index}`)),
      "Anhang konnte nicht geladen werden",
    );
    return res.blob();
  },
};
